import React, { useCallback, useEffect, useState, memo } from 'react'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import SideMenu from 'components/smart/side-menu/SideMenu'
import Header from 'components/smart/header/Header'
import { Icon, Typography } from '@mui/material'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'

// Mobile Block View = when mobile view is disabled for selected pages
const MobileBlockView = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: 'calc(100dvh - 140px)',
      border: '1px solid',
      borderColor: 'gray-line.main',
      borderRadius: '4.5px',
      overflow: 'clip',
    }}
  >
    <AdxEmptyStateV2
      icon={<Icon>mobile_off</Icon>}
      title="This section is not available on mobile view"
      subtitle="We apologize for the inconvenience. Please visit adnexioHR on dektop/laptop to access this feature"
      height="100%"
    />
  </Box>
)

const lgBreakpoint = (theme) => theme.breakpoints.up('lg')
const smBreakpoint = (theme) => theme.breakpoints.up('sm')

const MaintenanceModal = memo(({ reminderShown, onDismiss }) => {
  return (
    <AdxInfoModal
      variant={'warning'}
      muiIcon={'info'}
      iconColor="warning.main"
      open={reminderShown}
      firstBtnHandle={() => onDismiss(false)}
      title="Maintenance Reminder"
      subtitle="There will be a maintenance scheduled between 12:00 to 13:00 on Monday, please make sure you have completed your tasks before this time."
      footerButtons
      firstBtnLabel={'Got it'}
    />
  )
})

const ContentBox = memo(({ childrenMaxWidth, children, isLarge, headerTitle }) => {
  const boxStyles = {
    px: { xs: childrenMaxWidth ? 0 : 2, lg: childrenMaxWidth ? 0 : 3 },
    py: { lg: childrenMaxWidth ? 0 : 2 },
    gap: 2,
    pb: childrenMaxWidth ? 0 : 3,
    width: '100%',
  }

  return (
    <Box sx={boxStyles}>
      {!isLarge && headerTitle && !childrenMaxWidth && (
        <Typography fontWeight="bold" py={2}>
          {headerTitle}
        </Typography>
      )}
      {children}
    </Box>
  )
})

const DashboardContainer = ({
  children,
  withHeader = true,
  headerTitle = '',
  noPadding = false,
  sx,
  childrenMaxWidth = false,
  stickyHeader,
  blockMobile,
  isBeta,
}) => {
  const isLarge = useMediaQuery(lgBreakpoint)
  const isSmall = useMediaQuery(smBreakpoint)

  // Collapsible Menu Related
  const [menuShown, setMenuShown] = useState(() => {
    const saved = localStorage.getItem('menuShown')
    return saved !== null ? JSON.parse(saved) : true
  })

  // Maintenance Modal Related
  const [reminderShown, setReminderShown] = useState(() => {
    const saved = localStorage.getItem('reminderShown')
    return saved !== null ? JSON.parse(saved) : true
  })

  useEffect(() => {
    localStorage.setItem('menuShown', JSON.stringify(menuShown))
  }, [menuShown])

  useEffect(() => {
    localStorage.setItem('reminderShown', JSON.stringify(reminderShown))
  }, [reminderShown])

  const handleReminderDismiss = useCallback((value) => {
    setReminderShown(value)
  }, [])

  // Memoize headerProps
  const headerProps = React.useMemo(
    () => ({
      withHeader,
      title: headerTitle,
      stickyHeader,
      isBeta,
      childrenMaxWidth,
      menuShown,
      setMenuShown,
    }),
    [withHeader, headerTitle, stickyHeader, isBeta, childrenMaxWidth, menuShown]
  )

  // Memoize renderHeader
  const renderHeader = useCallback(() => {
    return <Header {...headerProps} />
  }, [headerProps])

  return (
    <Box
      minHeight="100vh"
      minWidth="320px"
      display={'flex'}
      flexDirection={'row'}
      bgcolor={'gray-background.main'}
      sx={{
        p: 0,
        height: '100vh',
        overflow: 'hidden',
        ...sx
      }}
    >
      {/* <MaintenanceModal reminderShown={reminderShown} onDismiss={handleReminderDismiss} /> */}
      <SideMenu menuShown={menuShown} setMenuShown={setMenuShown} />
      <Box
        ml={isLarge ? (menuShown ? '320px' : '100px') : 0}
        width={isLarge ? (menuShown ? 'calc(100% - 320px)' : 'calc(100% - 100px)') : '100%'}
      >
        {renderHeader()}
        <ContentBox childrenMaxWidth={childrenMaxWidth} isLarge={isLarge} headerTitle={headerTitle}>
          {blockMobile && !isSmall ? <MobileBlockView /> : children}
        </ContentBox>
      </Box>
    </Box>
  )
}

export default memo(DashboardContainer)
